import React from "react"
import { Plus, Trash2 } from "lucide-react"
import { Input } from "src/components/ui/input"
import { Textarea } from "src/components/ui/textarea"
import { Button } from "src/components/ui/button"
import { RATIOS, HEADING_LEVELS, SPACER_SIZES } from "./blockSchema"
import { ImagePickerField } from "./ImagePicker"

// A small labelled field wrapper.
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[12px] font-medium text-slate-600">{label}</span>
      {children}
    </label>
  )
}

function Segmented({ value, options, onChange }) {
  return (
    <div className="inline-flex rounded-lg border border-slate-200 p-0.5">
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          className={`rounded-md px-2.5 py-1 text-[12px] ${
            String(value) === String(opt) ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  )
}

function HeadingInspector({ props, set }) {
  return (
    <div className="space-y-3">
      <Field label="Text">
        <Input value={props.text || ""} onChange={(e) => set({ text: e.target.value })} />
      </Field>
      <Field label="Size">
        <Segmented value={props.level || 2} options={HEADING_LEVELS} onChange={(v) => set({ level: Number(v) })} />
      </Field>
    </div>
  )
}

function TextInspector({ props, set }) {
  return (
    <Field label="Text (basic HTML allowed: bold, links, lists)">
      <Textarea
        rows={6}
        value={props.html || ""}
        onChange={(e) => set({ html: e.target.value })}
        className="font-mono text-[13px]"
      />
    </Field>
  )
}

function ImageInspector({ props, set, images }) {
  return (
    <div className="space-y-3">
      <Field label="Image">
        <ImagePickerField multiple={false} images={images} value={props.image_id} onChange={(id) => set({ image_id: id })} />
      </Field>
      <Field label="Ratio">
        <Segmented value={props.ratio || "16:9"} options={RATIOS} onChange={(v) => set({ ratio: v })} />
      </Field>
    </div>
  )
}

function GalleryInspector({ props, set, images }) {
  return (
    <div className="space-y-3">
      <Field label="Images">
        <ImagePickerField multiple images={images} value={props.image_ids || []} onChange={(ids) => set({ image_ids: ids })} />
      </Field>
      <Field label="Ratio">
        <Segmented value={props.ratio || "16:9"} options={RATIOS} onChange={(v) => set({ ratio: v })} />
      </Field>
      <label className="flex items-center gap-2 text-[13px] text-slate-600">
        <input
          type="checkbox"
          checked={!!props.show_posted_date}
          onChange={(e) => set({ show_posted_date: e.target.checked })}
        />
        Show posted date overlay
      </label>
    </div>
  )
}

function SpecGridInspector({ props, set }) {
  const items = props.items || []
  const update = (i, key, val) => {
    const next = items.map((it, idx) => (idx === i ? { ...it, [key]: val } : it))
    set({ items: next })
  }
  const add = () => set({ items: [...items, { label: "", value: "" }] })
  const remove = (i) => set({ items: items.filter((_, idx) => idx !== i) })

  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input
            placeholder="Label"
            value={it.label || ""}
            onChange={(e) => update(i, "label", e.target.value)}
            className="flex-1"
          />
          <Input
            placeholder="Value"
            value={it.value || ""}
            onChange={(e) => update(i, "value", e.target.value)}
            className="flex-1"
          />
          <Button type="button" variant="ghost" size="icon" onClick={() => remove(i)} title="Remove">
            <Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-red-500" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" className="gap-1" onClick={add}>
        <Plus className="h-3.5 w-3.5" /> Add spec
      </Button>
    </div>
  )
}

function SpacerInspector({ props, set }) {
  return (
    <Field label="Size">
      <Segmented value={props.size || "md"} options={SPACER_SIZES} onChange={(v) => set({ size: v })} />
    </Field>
  )
}

const INSPECTORS = {
  heading: HeadingInspector,
  text: TextInspector,
  image: ImageInspector,
  image_gallery: GalleryInspector,
  spec_grid: SpecGridInspector,
  spacer: SpacerInspector,
}

// Renders the prop editor for the selected block. `images` = the project's
// available images for picker blocks. `onChange(nextProps)` is called with the
// merged props.
export function BlockInspector({ block, images, onChange }) {
  if (!block) {
    return <p className="text-[13px] text-slate-400">Select a block to edit it, or add one from the palette.</p>
  }
  const Comp = INSPECTORS[block.type]
  if (!Comp) return <p className="text-[13px] text-slate-400">This block has no settings.</p>

  const set = (patch) => onChange({ ...block.props, ...patch })
  return <Comp props={block.props || {}} set={set} images={images} />
}
